import { useState, useEffect, useRef } from "react";
import { Popper, Paper, Fade, Button } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { CalendarMonthOutlined } from "@mui/icons-material";
import { makeSxStyles } from "ui";
import CustomDay from "../../../shared/components/date-picker/CustomDay";
import { AdminGetRouteStatusesPerMonthResDto } from "service/api/estabrookSchemas";

interface CustomDatePickerProps {
  defaultDate?: DateTime;
  onDateChange: (date: DateTime | null) => void;
  onMonthChange?: (month: DateTime | null) => void;
  highlightedDays?: AdminGetRouteStatusesPerMonthResDto[];
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  defaultDate,
  onDateChange,
  onMonthChange,
  highlightedDays,
}) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(
    defaultDate || null,
  );
  const popperRef = useRef<HTMLDivElement | null>(null);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    setSelectedDate(defaultDate || null);
  }, [defaultDate]);

  const handleDateChange = (newDate: DateTime | null) => {
    setSelectedDate(newDate);
    onDateChange(newDate);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setAnchorEl(null);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl]);

  return (
    <>
      <Button
        variant="text"
        onClick={handleButtonClick}
        startIcon={<CalendarMonthOutlined />}
        sx={styles.button}
      >
        {selectedDate
          ? selectedDate.toFormat("EEE, dd LLLL yy")
          : "Select Date"}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        ref={popperRef}
        sx={{ zIndex: 100 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper elevation={3}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                {...(onMonthChange && { onMonthChange: onMonthChange })}
                slots={{
                  day: CustomDay,
                }}
                slotProps={{
                  day: {
                    sx: styles.dateCalendarDays,
                    highlightedDays,
                    selectedDay: selectedDate,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any,
                }}
              />
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default CustomDatePicker;

const useStyles = makeSxStyles(({ palette: { primary } }) => ({
  button: {
    color: primary.main,
    display: "flex",
    alignItems: "center",
  },
  dateCalendarDays: {
    "&.Mui-selected": {
      backgroundColor: "#004B6F",
    },
  },
}));
